import { getAssetTypeMaxCopyLength } from 'src/data/asset';
import {
  GENERATION_TOOL_GROUP,
  getGroupByTool,
  getToolDefaultAssetType,
  getToolLabel,
} from 'src/data/generationTool';
import type { UserSettings } from 'src/data/user';
import * as env from 'src/lib/env';
import { sendMessageToExtension } from 'src/lib/extensions';
import { sendMessageToParent } from 'src/lib/messages';

import {
  externalMessageLogin,
  externalMessageLogout,
  externalMessageSettingsChanged,
  messageSubmitInstructions,
} from './messages';
import { formatGenerationPrompt } from './prompt';
import type { InfinityGenerationParams, InfinitySubmitInstructionsParams } from './types';

/**
 * Send generated from params prompt to extension, asking it to replace current "prompt input" value
 * @param params - Generation params
 */
export const sendFullPromptToInfinityExtension = (params: InfinityGenerationParams) => {
  const text = formatGenerationPrompt(params);
  const message = messageSubmitInstructions({
    type: 'prompt',
    params: prepareGenerationParams(params),

    // 2023-07-17: deprecated, remove after Extension 0.0.26 is not in use anymore
    text,
    name: getToolLabel(params?.generationTool),
    generationTool: params?.generationTool,
    generationToolGroup: getGroupByTool(params?.generationTool),
  });
  sendMessageToParent(message);
};

export const syncAuthTokenToExternalExtension = (
  authToken: string | undefined
): Promise<unknown> => {
  const extensionId = env.getInfinityExtensionID();
  const message = authToken ? externalMessageLogin(authToken) : externalMessageLogout();
  return sendMessageToExtension(extensionId, message);
};

export const notifyExternalExtensionSettingsChanged = (
  settings: UserSettings
): Promise<unknown> => {
  const extensionId = env.getInfinityExtensionID();
  const message = externalMessageSettingsChanged(settings);
  return sendMessageToExtension(extensionId, message);
};

const prepareGenerationParams = (
  params: InfinityGenerationParams
): InfinitySubmitInstructionsParams => {
  const result: InfinitySubmitInstructionsParams = {
    ...params,
    generationToolLabel: getToolLabel(params.generationTool),
    generationToolGroup: getGroupByTool(params.generationTool),
    assetType: getToolDefaultAssetType(params.generationTool),
  };

  // TODO: fix for ALM-5989, consider removing limit for all tools without the "strict" limit
  if (result.generationToolGroup !== GENERATION_TOOL_GROUP.social && result.assetType) {
    result.maxAssetTypeLength = getAssetTypeMaxCopyLength(result.assetType);
  }

  return result;
};
